import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Database, Cloud, Server } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function LoginPage() {
  const [email, setEmail] = useState(() => localStorage.getItem("rememberedEmail") || "");
  const [password, setPassword] = useState(() => localStorage.getItem("rememberedPassword") || "");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem("rememberedEmail"));
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState(null);
  const [dbStatus, setDbStatus] = useState(null);
  const [showDbInfo, setShowDbInfo] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetch("/version.json?" + Date.now())
      .then(r => r.json())
      .then(d => setVersion(d.version))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const checkDb = () => {
      fetch(`${API}/api/health/db`)
        .then(r => r.json())
        .then(d => setDbStatus(d))
        .catch(() => setDbStatus({ connected: false }));
    };
    checkDb();
    const interval = setInterval(checkDb, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      if (rememberMe) {
        localStorage.setItem("rememberedEmail", email);
        localStorage.setItem("rememberedPassword", password);
      } else {
        localStorage.removeItem("rememberedEmail");
        localStorage.removeItem("rememberedPassword");
      }
      await login(email, password, rememberMe);
      toast.success("Login successful!");
      navigate("/app/log");
    } catch (error) {
      const message = error.response?.data?.detail || "Invalid email or password";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Helper: one row in the DB info popup
  const Row = ({ label, value, mono = true }) => (
    <div className="flex justify-between items-center gap-2">
      <span className="text-slate-400 shrink-0">{label}</span>
      <span className={`${mono ? "font-mono" : ""} text-slate-700 text-right truncate`} title={value}>
        {value ?? "—"}
      </span>
    </div>
  );

  return (
    <div className="h-screen overflow-hidden flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-12 overflow-y-auto">
        <div className="w-full max-w-md animate-fade-in">
          <div className="mb-8">
            <img src="/logo.png" alt="DailyLog" className="h-16 mb-4" />
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Welcome back
            </h1>
            <p className="mt-2 text-slate-600">
              Sign in to continue to DailyLog
            </p>
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl">Sign In</CardTitle>
              <CardDescription>Enter your credentials to access your account</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link
                      to="/forgot-password"
                      className="text-sm text-primary hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="remember-me"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                  />
                  <Label htmlFor="remember-me" className="text-sm text-slate-600 cursor-pointer font-normal">
                    Remember me
                  </Label>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </form>

              <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-400">
                <span>{version ? `v${version}` : ""}</span>

                {/* DB Status indicator */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowDbInfo(!showDbInfo)}
                    className="flex items-center gap-1.5 hover:text-slate-600 transition-colors"
                  >
                    <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                      dbStatus === null   ? "bg-yellow-400 animate-pulse" :
                      dbStatus.connected  ? "bg-emerald-500" : "bg-red-500"
                    }`} />
                    {dbStatus === null ? (
                      <span>Checking DB...</span>
                    ) : dbStatus.connected ? (
                      <span className="flex items-center gap-1">
                        {dbStatus.is_atlas
                          ? <Cloud className="h-3 w-3" />
                          : <Server className="h-3 w-3" />}
                        {dbStatus.connection_type}
                        <span className="text-slate-300">·</span>
                        <span className="font-mono">{dbStatus.dbName}</span>
                      </span>
                    ) : (
                      <span className="text-red-400">DB Offline</span>
                    )}
                  </button>

                  {/* Detail popup */}
                  {showDbInfo && dbStatus && (
                    <div className="absolute bottom-full right-0 mb-2 bg-white border border-slate-200 rounded-lg shadow-xl p-3 text-xs text-slate-600 w-64 z-50">
                      {/* Header */}
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
                        <Database className="h-3.5 w-3.5 text-slate-500" />
                        <span className="font-semibold text-slate-800">
                          {dbStatus.connected ? "Connected" : "Not Connected"}
                        </span>
                        <span className={`ml-auto px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          dbStatus.is_atlas
                            ? "bg-blue-50 text-blue-600"
                            : "bg-slate-100 text-slate-600"
                        }`}>
                          {dbStatus.connection_type || "Unknown"}
                        </span>
                      </div>

                      <div className="space-y-1.5">
                        <Row label="ENV Config" value={dbStatus.env_source} mono={false} />
                        <Row label="ENV Mode"   value={dbStatus.env_mode} />
                        <Row label="Host"       value={dbStatus.host} />
                        <Row label="Port"       value={dbStatus.is_atlas ? "SRV (auto)" : String(dbStatus.port ?? 27017)} />
                        <Row label="Database"   value={dbStatus.dbName} />
                        {dbStatus.connected && <>
                          <div className="border-t border-slate-100 my-1" />
                          <Row label="Size"        value={`${dbStatus.sizeMB} MB`} />
                          <Row label="Collections" value={String(dbStatus.collections)} />
                          <Row label="Total Docs"  value={dbStatus.totalDocs?.toLocaleString()} />
                        </>}
                        {!dbStatus.connected && dbStatus.error && (
                          <div className="mt-2 p-2 bg-red-50 rounded text-red-600 text-[10px] font-mono break-all">
                            {dbStatus.error.slice(0, 120)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-sm text-slate-500">
            Don't have an account?{" "}
            <span className="text-slate-700">Contact your admin for an invite link.</span>
          </p>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to bottom right, rgba(79, 70, 229, 0.1), rgba(79, 70, 229, 0.05)), url('https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2')`,
        }}
      >
        <div className="h-full flex items-end p-12">
          <div className="bg-white/90 backdrop-blur-sm rounded-lg p-6 max-w-md">
            <p className="text-lg font-medium text-slate-800">
              "DailyLog has transformed how our team tracks progress. Simple, effective, and keeps everyone accountable."
            </p>
            <p className="mt-3 text-sm text-slate-600">— Team Lead, JobTalk</p>
          </div>
        </div>
      </div>
    </div>
  );
}
